import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put, errorMessage } from '@/shared/api/client';
import { Alert, Badge, Button, Card, Field, Input, Skeleton, Table } from '@/shared/ui';
import { useToast } from '@/app/providers';

type Cat = { id: number; code: string; name: string; is_active: boolean; sort_order: number; rules?: Record<string, unknown> | null };

function CatalogCard({ title, listPath, putPath, withRules }: { title: string; listPath: string; putPath: string; withRules: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['catalog', listPath], queryFn: () => get<Cat[]>(`${listPath}?include_inactive=true`) });
  const [form, setForm] = useState<{ code: string; name: string; sort_order: string; is_active: boolean; rules: string }>({ code: '', name: '', sort_order: '0', is_active: true, rules: '{}' });
  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { code: form.code.trim().toUpperCase(), name: form.name.trim(), sort_order: Number.parseInt(form.sort_order || '0', 10), is_active: form.is_active };
      if (withRules) body.rules = JSON.parse(form.rules || '{}');
      return put(putPath, body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['catalog', listPath] }); toast.notify({ title: 'Categoría guardada' }); setForm({ code: '', name: '', sort_order: '0', is_active: true, rules: '{}' }); },
    onError: (e) => toast.notify({ title: 'No se pudo guardar', description: e instanceof SyntaxError ? 'Las reglas deben ser JSON válido' : errorMessage(e), kind: 'error' }),
  });
  return (
    <Card title={title}>
      {q.isPending && <Skeleton height={80} />}
      {q.isError && <Alert kind="error">{errorMessage(q.error)}</Alert>}
      {q.data && (
        <Table>
          <thead><tr><th>Código</th><th>Nombre</th><th>Orden</th><th>Estado</th>{withRules && <th>Reglas</th>}<th></th></tr></thead>
          <tbody>
            {q.data.map((c) => (
              <tr key={c.id}>
                <td><code style={{ fontSize: 'var(--fs-xs)' }}>{c.code}</code></td><td>{c.name}</td><td className="num">{c.sort_order}</td>
                <td><Badge tone={c.is_active ? 'ok' : 'neutral'}>{c.is_active ? 'Activa' : 'Inactiva'}</Badge></td>
                {withRules && <td style={{ fontSize: 'var(--fs-xs)' }}><code>{JSON.stringify(c.rules ?? {})}</code></td>}
                <td><Button size="sm" variant="ghost" onClick={() => setForm({ code: c.code, name: c.name, sort_order: String(c.sort_order), is_active: c.is_active, rules: JSON.stringify(c.rules ?? {}) })}>Editar</Button></td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} style={{ marginTop: 'var(--sp-4)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--sp-3)', alignItems: 'end' }}>
        <Field label="Código" htmlFor={`${putPath}-code`} hint="MAYUSCULAS_Y_GUION_BAJO"><Input id={`${putPath}-code`} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
        <Field label="Nombre" htmlFor={`${putPath}-name`}><Input id={`${putPath}-name`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Orden" htmlFor={`${putPath}-order`}><Input id={`${putPath}-order`} inputMode="numeric" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value.replace(/[^0-9]/g, '') })} /></Field>
        {withRules && <Field label="Reglas (JSON)" htmlFor={`${putPath}-rules`}><Input id={`${putPath}-rules`} value={form.rules} onChange={(e) => setForm({ ...form, rules: e.target.value })} /></Field>}
        <label style={{ fontSize: 'var(--fs-sm)', display: 'flex', gap: 8, alignItems: 'center', marginBottom: 'var(--sp-4)' }}><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Activa</label>
        <Button type="submit" loading={save.isPending} disabled={!form.code || !form.name} style={{ marginBottom: 'var(--sp-4)' }}>Guardar</Button>
      </form>
    </Card>
  );
}

export function CatalogsPage() {
  return (
    <>
      <h1 style={{ marginBottom: 'var(--sp-5)' }}>Categorías</h1>
      <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
        <CatalogCard title="Categorías de gasto (§13.1)" listPath="/catalogs/expense-categories" putPath="/admin/expense-categories" withRules />
        <CatalogCard title="Categorías internas de imputación (§7.4)" listPath="/catalogs/internal-categories" putPath="/admin/internal-categories" withRules={false} />
      </div>
    </>
  );
}
